import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { endOfDay, format, isValid, parseISO, startOfDay } from 'date-fns';
import { PasswordFormData } from '../components/Compounds/Modals/ResetPassword';
import {
  InventoryAsset,
  SupportCasesItem,
  GroupedCases,
  CasesBarItem,
  PasswordValidation,
  WidgetCustomization
} from '../types';
import { CustomView, getUserAccountById, GridPreferences, PrefKey, UserPreferencesContent } from '../store/User';
import { Project } from '../store/Project';
import { ApiAdvancedFilter, FilterableField } from '../store/GeneralStore';
import { getOriginLabel, isConvertedRecord, isUpstackSourced } from './originLabel';
import { isMonthToMonthTerm, MONTH_TO_MONTH_TERM, termLabel } from './term';

export const createOrderFromIdDate = (id: string, date: Date) => ({
  id: id,
  name: 'name',
  order_number: '11',
  status: 'Pending Install',
  start_date: date.toISOString().slice(0, 10),
  bandwidth: '',
  contractEndDate: '',
  supplier: { id: '2', name: '2' },
  address: 'Address',
  nRC: 10,
  mRC: 20,
  term: '3 Months',
  orderType: '',
  product: { id: '3', name: '3' }
});

export const createInventoryFromId = (id: string, opts: Partial<Omit<InventoryAsset, 'Id'>> = {}): InventoryAsset => ({
  id: id,
  sfId: '',
  address: 'Address',
  addressInfo: {
    address: '',
    locationName: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    latitude: 20,
    longitude: 30,
    number: '1'
  },
  bandwidth: '1',
  billingFrequency: '',
  cageRackID: '',
  circuitIDs: '',
  circuitType: '',
  completionDate: '',
  serviceActivationDate: '',
  contractEndDate: '',
  iPDetails: '',
  kwRack: 1.0,
  lecCircuitID: '',
  locationContact: { name: '', email: '', phone: '' },
  mRC: 2.0,
  nRC: 3.0,
  orderType: '',
  phoneNumbers: undefined,
  product: { id: '', name: '' },
  rackType: '',
  status: '',
  supplier: { id: '', name: '' },
  term: '4 Months',
  locationAId: '',
  locationALecCircuitId: '',
  locationALecDemarc: '',
  locationALocalLec: '',
  locationAText: '',
  locationId: '',
  locationZId: '',
  locationZLecCircuitId: '',
  locationZLecDemarc: '',
  locationZLocalLec: '',
  locationZText: '',
  locationZName: '',
  locationZLatitude: 0,
  locationZLongitude: 0,
  connectivityA: '',
  connectivityB: '',
  equipmentManufacturer: '',
  equipmentType: '',
  equipmentDetails: '',
  accessSpeed: '',
  portSpeed: '',
  disconnectedReason: '',
  accessType: '',
  firstInvoiceReviewedByDate: '',
  disconnectedDate: '',
  lecDemarc: '',
  accessProvider: '',
  providerAccountNumber: '',
  supplierOrderNumber: '',
  ...opts
});

export const monthsOptions = [{ value: '1', text: 'This Month' }].concat(
  [2, 4, 6, 9, 12].map((n) => {
    return { value: String(n), text: `${n} Months` };
  })
);

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function getFirstAndLastDayOfMonth(shortMonth: string): [firstDay: Date, lastDay: Date] {
  const monthNames: { [key: string]: number } = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11
  };
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const monthIndex = monthNames[shortMonth];
  const year = currentMonth >= monthIndex ? currentYear : currentYear - 1;
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  return [firstDay, lastDay];
}

export function filteredCasesByStatusMonth(cases: SupportCasesItem[], months: number): SupportCasesItem[] {
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();
  const startDate = new Date(currentYear, currentMonth - months, 1);
  const endDate = new Date(currentYear, currentMonth, 0);
  return cases.filter((case_) => {
    const caseDate = new Date(case_.dateTimeOpened);
    return (
      !['Escalated', 'Closed (Resolved/ Unconfirmed)', 'Merged', 'Closed'].includes(case_.status) &&
      caseDate >= startDate &&
      caseDate <= endDate
    );
  });
}

export function filteredClosedCasesByMonth(cases: SupportCasesItem[], months: number): SupportCasesItem[] {
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();
  const startDate = new Date(currentYear, currentMonth - months, 1);
  const endDate = new Date(currentYear, currentMonth, 0);
  return cases.filter((case_) => {
    const caseDate = case_.dateTimeClosed ? new Date(case_.dateTimeClosed) : null;
    return (
      ['Closed (Resolved/ Unconfirmed)', 'Merged', 'Closed'].includes(case_.status) &&
      caseDate &&
      caseDate >= startDate &&
      caseDate <= endDate
    );
  });
}

function dateByStatus(item: SupportCasesItem): string {
  if (['Escalated', 'Closed (Resolved/ Unconfirmed)', 'Merged', 'Closed'].includes(item.status)) {
    return item.dateTimeClosed || '';
  } else {
    return item.dateTimeOpened;
  }
}

export function groupByMonthNumber(items: SupportCasesItem[]): GroupedCases[] {
  const groups: { [key: string]: number } = {};
  for (const item of items) {
    const date = new Date(dateByStatus(item));
    const month = `${date.getFullYear()}-${date.getMonth() + 1}`;
    if (groups[month] === undefined) {
      groups[month] = 1;
    } else {
      groups[month]++;
    }
  }
  const sortedGroups = Object.entries(groups)
    .map(([month, count]) => ({ month, count }))
    .sort((a, b) => {
      const [aYear, aMonth] = a.month.split('-').map(Number);
      const [bYear, bMonth] = b.month.split('-').map(Number);
      return aYear !== bYear ? aYear - bYear : aMonth - bMonth;
    });
  return sortedGroups;
}

function currentMonthByMonths(months: number): Date {
  const currentMonth = new Date();
  currentMonth.setMonth(currentMonth.getMonth() - (months - 1));
  return currentMonth;
}

function monthLabel(currentMonth: Date): string {
  return `${currentMonth.getFullYear()}-${currentMonth.getMonth() + 1}`;
}

function shortMonthName(currentMonth: Date): string {
  return currentMonth.toLocaleString('default', { month: 'short' });
}

function generateChartData(
  months: number,
  currentMonth: Date,
  groupedOpenCases: GroupedCases[],
  groupedClosedCases: GroupedCases[]
): CasesBarItem[] {
  const chartData: CasesBarItem[] = [];
  for (let i = 0; i < months; i++) {
    const monthName = shortMonthName(currentMonth);
    const matchingOpenGroup = groupedOpenCases.find((group) => {
      return group.month === monthLabel(currentMonth);
    });
    const matchingClosedGroup = groupedClosedCases.find((group) => {
      return group.month === monthLabel(currentMonth);
    });
    chartData.push({
      name: monthName,
      'Closed Cases': matchingClosedGroup?.count || 0,
      'Open Cases': matchingOpenGroup?.count || 0
    });
    currentMonth.setMonth(currentMonth.getMonth() + 1);
  }
  return chartData;
}

export const getDate = (dateString: unknown): Date => {
  if (typeof dateString !== 'string' || !dateString) return new Date(NaN);
  // new Date reads 'YYYY-MM-DD' as UTC midnight, parseISO as the local day; shapes it can't read fall back.
  const iso = parseISO(dateString);
  return isValid(iso) ? iso : new Date(dateString);
};

// A due date stays due through its whole day.
export const isPastDue = (dueDate: unknown, now = new Date()): boolean => getDate(dueDate) < startOfDay(now);

export function casesChartData(cases: SupportCasesItem[], months: number): CasesBarItem[] {
  const groupedOpenCases = groupByMonthNumber(filteredCasesByStatusMonth(cases, months));
  const groupedClosedCases = groupByMonthNumber(filteredClosedCasesByMonth(cases, months));
  const currentMonth = currentMonthByMonths(months);
  return generateChartData(months, currentMonth, groupedOpenCases, groupedClosedCases);
}

export function parseQuarterToDates(quarterString: string): [Date, Date] {
  const year = Number(20 + quarterString.slice(0, 2));
  const quarter = Number(quarterString.slice(-1));
  if (quarter < 1 || quarter > 4) throw new Error('Quarter number must be between 1 and 4');

  return [new Date(year, quarter * 3 - 3, 1), endOfDay(new Date(year, quarter * 3, 0))];
}

export function filteredByQuarterInventoryAssets(
  filteredInventoryAssets: InventoryAsset[],
  chartQuarter: string
): InventoryAsset[] {
  return filteredInventoryAssets
    .filter((inventoryAsset) => {
      const contractEndDate = getDate(inventoryAsset.contractEndDate);
      if (!chartQuarter) return true;

      const [startDate, endDate] = parseQuarterToDates(chartQuarter);
      return startDate <= contractEndDate && contractEndDate < endDate;
    })
    .sort((a, b) => getDate(a.contractEndDate).getTime() - getDate(b.contractEndDate).getTime());
}

const dataGroupedByQuarters = (filteredInventoryAssets: InventoryAsset[]) => {
  return filteredInventoryAssets.reduce(
    (acc, inventoryAsset) => {
      const contractEndDate = getDate(inventoryAsset.contractEndDate);
      const quarter = Math.floor((contractEndDate.getMonth() + 3) / 3);
      const year = contractEndDate.getFullYear() % 2000;
      const key = `${year} ${quarter}`;
      if (acc[key]) {
        acc[key] += 1;
      } else {
        acc[key] = 1;
      }
      return acc;
    },
    {} as { [key: string]: number }
  );
};

const simplifiedKey = (key: string) => {
  const [year, quarter] = key.split(' ');
  return `${year} Q${quarter}`;
};

export const lineDataWithSimplifiedKey = (filteredInventoryAssets: InventoryAsset[]) => {
  const dataGroupedByQuarter = dataGroupedByQuarters(filteredInventoryAssets);

  const lineData = Object.keys(dataGroupedByQuarter).map((key) => {
    return { name: key, Expirations: dataGroupedByQuarter[key] };
  });

  const lineDataSortedByNames = lineData.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  return lineDataSortedByNames.map((lineData) => {
    return { ...lineData, name: simplifiedKey(lineData.name) };
  });
};

export const filterInventoryAssets = (inventoryAssets: InventoryAsset[], selectedServiceTypes: string[]) => {
  return inventoryAssets.filter(
    (inventoryAsset) =>
      !!inventoryAsset.addressInfo.latitude &&
      !!inventoryAsset.addressInfo.longitude &&
      !!inventoryAsset.address &&
      selectedServiceTypes.includes(inventoryAsset.product?.family || '') &&
      FILTER_SERVICE_CATEGORIES.includes(inventoryAsset.product?.family || '')
  );
};

export const groupInventoryAssetsByAddress = (filteredAssets: InventoryAsset[]) => {
  return filteredAssets.reduce(
    (acc, asset) => {
      const address = asset.address;
      if (!address) return acc;

      const existingGroup = acc.find((group) => group[0] === address);
      if (existingGroup) {
        existingGroup[1].push(asset);
      } else {
        acc.push([address, [asset]]);
      }
      return acc;
    },
    [] as [string, InventoryAsset[]][]
  );
};

export const filterEmptyGroups = (groupedAssets: [string, InventoryAsset[]][]) => {
  return groupedAssets.filter((group) => group[1].length > 0);
};

export const coordKey = (lat: number, lng: number) => `${lat.toFixed(5)},${lng.toFixed(5)}`;

// Merges address groups that share the same coordinates (to 5 decimal places) into a single
// pin. This handles assets at the same physical location that have slightly different address
// strings — e.g. "5900 N Andrews Ave, Ste 700" vs "5900 North Andrews Avenue" — so they all
// appear under one map marker instead of separate dots.
export const mergeGroupsByCoordinates = (groups: [string, InventoryAsset[]][]): [string, InventoryAsset[]][] => {
  const merged = new Map<string, [string, InventoryAsset[]]>();
  const noCoordGroups: [string, InventoryAsset[]][] = [];

  for (const [address, assets] of groups) {
    const lat = assets[0]?.addressInfo?.latitude;
    const lng = assets[0]?.addressInfo?.longitude;
    if (!lat || !lng) {
      noCoordGroups.push([address, [...assets]]);
      continue;
    }
    const key = coordKey(lat, lng);
    const existing = merged.get(key);
    if (existing) {
      existing[1].push(...assets);
    } else {
      merged.set(key, [address, [...assets]]);
    }
  }

  return [...Array.from(merged.values()), ...noCoordGroups];
};

// Returns a normalized 4-token key from an address string for fuzzy matching.
// Lowercases, strips punctuation, and takes the first 4 whitespace-separated tokens.
// "1 Oliver's Yard, 5th Floor..." and "1 Oliver's Yard 5th Floor..." both → "1 olivers yard 5th"
const normalizeAddressKey = (address: string): string =>
  address
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 4)
    .join(' ');

export const generateMapData = (
  inventoryAssets: InventoryAsset[],
  selectedProductNames: string[]
): [string, InventoryAsset[]][] => {
  // Build fallback coordinate lookup from Z location data, keyed two ways:
  // 1. By SF location ID (exact match, most reliable)
  // 2. By normalized address prefix (fuzzy match for same location with different SF records)
  const zCoordsByLocationId = new Map<string, { latitude: number; longitude: number }>();
  const zCoordsByAddressKey = new Map<string, { latitude: number; longitude: number }>();
  for (const asset of inventoryAssets) {
    if (asset.locationZLatitude && asset.locationZLongitude) {
      if (asset.locationZId) {
        zCoordsByLocationId.set(asset.locationZId, {
          latitude: asset.locationZLatitude,
          longitude: asset.locationZLongitude
        });
      }
      if (asset.locationZText) {
        zCoordsByAddressKey.set(normalizeAddressKey(asset.locationZText), {
          latitude: asset.locationZLatitude,
          longitude: asset.locationZLongitude
        });
      }
    }
  }

  const getFallbackCoords = (asset: InventoryAsset) => {
    if (asset.locationAId && zCoordsByLocationId.has(asset.locationAId)) {
      return zCoordsByLocationId.get(asset.locationAId)!;
    }
    if (asset.address) {
      const key = normalizeAddressKey(asset.address);
      if (zCoordsByAddressKey.has(key)) return zCoordsByAddressKey.get(key)!;
    }
    return null;
  };

  const selectedProductNamesSet = new Set(selectedProductNames);
  const filteredAssets = inventoryAssets
    .filter((asset) => {
      const hasCoords = !!asset.addressInfo.latitude && !!asset.addressInfo.longitude;
      const hasFallback = !!getFallbackCoords(asset);
      return (
        (hasCoords || hasFallback) &&
        !!asset.address &&
        selectedProductNamesSet.has(asset.product?.name || '') &&
        FILTER_SERVICE_CATEGORIES.includes(asset.product?.family || '')
      );
    })
    .map((asset) => {
      if (!!asset.addressInfo.latitude && !!asset.addressInfo.longitude) return asset;
      const fallback = getFallbackCoords(asset)!;
      return { ...asset, addressInfo: { ...asset.addressInfo, ...fallback } };
    });

  const groupedAssets = groupInventoryAssetsByAddress(filteredAssets);
  return filterEmptyGroups(mergeGroupsByCoordinates(groupedAssets));
};

export const generateALocationMapData = (
  inventoryAssets: InventoryAsset[],
  selectedProductNames: string[]
): [string, InventoryAsset[]][] => {
  const selectedProductNamesSet = new Set(selectedProductNames);
  const filteredAssets = inventoryAssets.filter(
    (asset) =>
      !!asset.locationALatitude &&
      !!asset.locationALongitude &&
      !!asset.locationAText &&
      selectedProductNamesSet.has(asset.product?.name || '') &&
      FILTER_SERVICE_CATEGORIES.includes(asset.product?.family || '') &&
      // Exclude assets where A location is the same as the install location (already shown via addressInfo markers).
      // Use a small epsilon to handle float precision differences from different data sources.
      (Math.abs(asset.locationALatitude - (asset.addressInfo.latitude ?? 0)) > 1e-5 ||
        Math.abs(asset.locationALongitude - (asset.addressInfo.longitude ?? 0)) > 1e-5)
  );
  const groupedMap = new Map<string, InventoryAsset[]>();
  for (const asset of filteredAssets) {
    const key = asset.locationAText;
    if (!key) continue;
    const existing = groupedMap.get(key);
    if (existing) {
      existing.push(asset);
    } else {
      groupedMap.set(key, [asset]);
    }
  }
  const groupedAssets: [string, InventoryAsset[]][] = Array.from(groupedMap.entries());
  return filterEmptyGroups(groupedAssets);
};

export const generateZLocationMapData = (
  inventoryAssets: InventoryAsset[],
  selectedProductNames: string[]
): [string, InventoryAsset[]][] => {
  const selectedProductNamesSet = new Set(selectedProductNames);
  const filteredAssets = inventoryAssets.filter(
    (asset) =>
      !!asset.locationZLatitude &&
      !!asset.locationZLongitude &&
      !!asset.locationZText &&
      selectedProductNamesSet.has(asset.product?.name || '') &&
      FILTER_SERVICE_CATEGORIES.includes(asset.product?.family || '')
  );
  const groupedMap = new Map<string, InventoryAsset[]>();
  for (const asset of filteredAssets) {
    const key = asset.locationZText;
    if (!key) continue;
    const existing = groupedMap.get(key);
    if (existing) {
      existing.push(asset);
    } else {
      groupedMap.set(key, [asset]);
    }
  }
  const groupedAssets: [string, InventoryAsset[]][] = Array.from(groupedMap.entries());
  return filterEmptyGroups(groupedAssets);
};

export const generateDoughnutData = (inventoryAssets: InventoryAsset[]) => {
  const active = inventoryAssets.filter((asset) => {
    return asset.status === 'Active';
  }).length;
  const pendingInstall = inventoryAssets.filter((asset) => asset.status === 'Pending Install').length;
  const cancelled = inventoryAssets.filter((asset) => asset.status === 'Cancelled').length;
  const pendingDisconnect = inventoryAssets.filter((asset) => asset.status === 'Pending Disconnect').length;
  const disconnected = inventoryAssets.filter((asset) => asset.status === 'Disconnected').length;
  const installed = inventoryAssets.filter((asset) => asset.status === 'Installed').length;
  const total = active + pendingInstall + cancelled + pendingDisconnect + disconnected + installed;
  return [
    { name: 'Active', value: active, fill: '#3B6FE8', percent: Math.round((active / total) * 100) },
    {
      name: 'Pending Install',
      value: pendingInstall,
      fill: '#D96E2B',
      percent: Math.round((pendingInstall / total) * 100)
    },
    { name: 'Cancelled', value: cancelled, fill: '#B8D936', percent: Math.round((cancelled / total) * 100) },
    {
      name: 'Pending Disconnect',
      value: pendingDisconnect,
      fill: '#454545',
      percent: Math.round((pendingDisconnect / total) * 100)
    },
    { name: 'Disconnected', value: disconnected, fill: '#9EA688', percent: Math.round((disconnected / total) * 100) },
    { name: 'Installed', value: installed, fill: '#85B9C1', percent: Math.round((installed / total) * 100) }
  ];
};

export const generateProjectStatusDoughnutData = (projects: Project[]) => {
  const escalated = projects.filter((asset) => {
    return asset.status === 'Escalated';
  }).length;
  const onHold = projects.filter((asset) => asset.status === 'On Hold').length;
  const inProgress = projects.filter((asset) => asset.status === 'In Progress').length;
  const installed = projects.filter((asset) => asset.status === 'Installed').length;
  const total = escalated + onHold + inProgress + installed;
  return [
    { name: 'Escalated', value: escalated, fill: '#D96E2B', percent: Math.round((escalated / total) * 100) },
    {
      name: 'On Hold',
      value: onHold,
      fill: '#DABD2C',
      percent: Math.round((onHold / total) * 100)
    },
    {
      name: 'In Progress',
      value: inProgress,
      fill: '#3B6FE8',
      percent: Math.round((inProgress / total) * 100)
    },
    { name: 'Installed', value: installed, fill: '#B8D936', percent: Math.round((installed / total) * 100) }
  ];
};

export const generateProjectPipelineDoughnutData = (projects: Project[]) => {
  const colorScale = [
    '#D96E2B',
    '#5C85E5',
    '#9EA688',
    '#B8D936',
    '#8777D9',
    '#DABD2C',
    '#FCCE7E',
    '#3F527F',
    '#4B39A2',
    '#F5E389'
  ];
  const projectProducts = projects.reduce((prods, proj): string[] => {
    const productName = proj.product?.nameAbbreviation || proj.product?.name || null;
    if (productName && !prods.includes(productName)) {
      prods.push(productName);
    }
    return prods;
  }, [] as string[]);

  const total = projects.length;
  return projectProducts.map((product, index) => {
    const projectCount = projects.filter((proj) => {
      const projProductName = proj.product?.nameAbbreviation || proj.product?.name || null;
      return projProductName === product;
    }).length;
    return {
      name: product,
      value: projectCount,
      fill: colorScale[index % colorScale.length],
      percent: Math.round((projectCount / total) * 100)
    };
  });
};

export const generateSuppliersDoughnutData = (inventoryAssets: InventoryAsset[]) => {
  const supplierAssets = inventoryAssets.filter((a) => !!a.supplier?.name);
  const supplierMap = supplierAssets.reduce((acc: Record<string, number>, asset) => {
    const supplierName = asset.supplier.name as string;
    acc[supplierName] = (acc[supplierName] || 0) + 1;
    return acc;
  }, {});
  const supplierEntries = Object.entries(supplierMap) as [string, number][];
  const totalCount = supplierAssets.length;
  supplierEntries.sort((a, b) => b[1] - a[1]);
  const topSuppliers = [];
  let otherCount = 0;
  type ColorMap = { [key: string]: string };
  const colors: ColorMap = {
    '0': '#D3D3D3',
    '1': '#3B6FE8',
    '2': '#D96E2B',
    '3': '#B8D936'
  };

  for (let i = 0; i < supplierEntries.length; i++) {
    const [supplierName, count] = supplierEntries[i];
    const percent = Math.round((count / totalCount) * 100);
    if (i < Math.min(4, supplierEntries.length)) {
      topSuppliers.push({
        name: supplierName,
        fill: colors[i.toString()],
        percent
      });
    } else {
      otherCount += count;
    }
  }

  if (otherCount > 0) {
    topSuppliers.push({ name: 'Other', fill: '#9EA688', percent: Math.round((otherCount / totalCount) * 100) });
  }

  return topSuppliers;
};

export const topSuppliers = (inventoryAssets: InventoryAsset[]) => {
  const supplierAssets = inventoryAssets.filter((a) => !!a.supplier?.name);
  const supplierMap = supplierAssets.reduce((acc: Record<string, number>, asset) => {
    const supplierName = asset.supplier.name as string;
    acc[supplierName] = (acc[supplierName] || 0) + 1;
    return acc;
  }, {});
  const supplierEntries = Object.entries(supplierMap) as [string, number][];
  supplierEntries.sort((a, b) => b[1] - a[1]);
  const totalCount = supplierAssets.length;
  const topSuppliers = [];
  for (let i = 0; i < supplierEntries.length; i++) {
    const [supplierName, count] = supplierEntries[i];
    const percent = Math.round((count / totalCount) * 100);
    topSuppliers.push({
      name: supplierName,
      percent
    });
  }
  return topSuppliers;
};

export const statusFormatter = (value: string) => {
  switch (value) {
    case 'Commissioning':
    case 'Completed':
      return 'Active';
    case 'Installed':
      return 'Installed';
    case 'Booked':
    case 'Pending Install':
      return 'Pending Install';
    case 'Cancelled':
    case 'Lost':
      return 'Cancelled';
    case 'Disconnected':
      return 'Disconnected';
    default:
      return value || 'In Progress';
  }
};

export { MONTH_TO_MONTH_TERM, isMonthToMonthTerm, termLabel };

export const termFormatter = (value: string | number | null) => {
  if (value === null) return '';
  if (isMonthToMonthTerm(value)) return MONTH_TO_MONTH_TERM;
  return `${value} Months`;
};

export const usaCurrency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export const formatCurrency = (
  amount: number | null | undefined,
  options?: { decimals?: number; zeroDisplay?: string }
): string => {
  const { decimals = 2, zeroDisplay = '$0' } = options ?? {};
  if (amount === null || amount === undefined || isNaN(amount) || amount === 0) return zeroDisplay;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(amount);
};

export function formatDate(d: unknown, isTime = false) {
  if (d === null || d === undefined || d === '') return '';
  // Stored dates are strings, but imported JSON can hold a number; show it as stored instead of throwing.
  if (typeof d === 'number') return Number.isFinite(d) ? String(d) : '';
  if (typeof d !== 'string') return '';

  if (isTime) {
    const date = d.split('/');
    const details = date[2].split(' ');
    const day = date[1].padStart(2, '0');
    const month = date[0].padStart(2, '0');
    const time = details[1].split(':');
    const hh = time[0].padStart(2, '0');

    return `${month}/${day}/${details[0]} ${hh}:${time[1]} ${details[2]}`;
  }

  const trimmed = d.trim();
  // Some date fields arrive as datetimes ("2026-08-14 00:00:00" or "2026-08-14T00:00:00Z"); keep the date part only.
  const datePart = trimmed.split(/[T\s]/)[0];
  // A calendar date typed as M/D/YYYY is padded, not parsed, so no timezone can move it.
  const typed = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (typed) return `${typed[1].padStart(2, '0')}/${typed[2].padStart(2, '0')}/${typed[3]}`;
  // Values that are not date-shaped (free text such as NA) pass through trimmed but untouched.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return trimmed;

  const [year, month, day] = datePart.split('-');
  return `${month}/${day}/${year}`;
}

// Matches the label rather than the raw term: an outside-inventory contract has a real end date
// to show even when its term is month-to-month, and only Upstack-managed terms reach here as a label.
export const formatContractEndDate = (date: string, termLength: string): string => {
  if (termLength === MONTH_TO_MONTH_TERM || date === MONTH_TO_MONTH_TERM) {
    return MONTH_TO_MONTH_TERM;
  }
  if (!date) return '';
  if (date === 'NA') return 'NA';
  const contractEndDate = getDate(date);
  if (!isValid(contractEndDate)) return 'NA';
  const fiftyYearsFromNow = new Date();
  fiftyYearsFromNow.setFullYear(fiftyYearsFromNow.getFullYear() + 50);
  if (contractEndDate > fiftyYearsFromNow) {
    return MONTH_TO_MONTH_TERM;
  }
  const hundredYearsAgo = new Date();
  hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);
  if (contractEndDate < hundredYearsAgo) return 'NA';
  return format(contractEndDate, 'MM/dd/yyyy');
};

// The column and its filter options sort the MM/dd/yyyy label, which as text orders by month before year.
export const contractEndDateSortKey = (label: string) => {
  const [month, day, year] = label.split('/');
  return year ? `${year}-${month}-${day}` : label;
};

export const compareContractEndDates = (a: string | number, b: string | number) =>
  contractEndDateSortKey(String(a)).localeCompare(contractEndDateSortKey(String(b)));

export function formatInfo(obj: string) {
  return <span style={{ whiteSpace: 'pre-wrap' }}>{obj}</span>;
}

export const getNestedValue = (obj: object, keyStr: string) => {
  const nestedVal = keyStr.split('.').reduce((o: object, i: string) => o?.[i as keyof typeof o], obj);
  return typeof nestedVal === 'string' ? nestedVal : '';
};

export const truncateStr = (val: string, maxLength = 20) =>
  val.length > maxLength ? `${val.substring(0, maxLength)}...` : val;

export const COLOCATION_SERVICE_CATEGORY = 'Colocation';
export const ROUTE1_COLOCATION_SERVICE_CATEGORY = 'Data Center and Cloud';
export const NETWORK_SERVICE_CATEGORY = 'Network';
export const ROUTE1_NETWORK_SERVICE_CATEGORY = 'Networking';
export const COMMUNICATIONS = 'Communications';
export const SOFTWARE_DEFINED_NETWORK = 'Software Defined Networking';
export const FILTER_SERVICE_CATEGORIES = [
  COLOCATION_SERVICE_CATEGORY,
  ROUTE1_COLOCATION_SERVICE_CATEGORY,
  NETWORK_SERVICE_CATEGORY,
  ROUTE1_NETWORK_SERVICE_CATEGORY,
  COMMUNICATIONS,
  SOFTWARE_DEFINED_NETWORK
];

export const objectNotEmpty = (obj: object) => !!Object.keys(obj).length;

export const getCaseClosedDate = (case_: SupportCasesItem): string => {
  const { dateTimeClosed } = case_;

  if (!dateTimeClosed) return '';

  return formatSupportCaseDate(dateTimeClosed);
};

export const formatSupportCaseDate = (rawDate: string): string => {
  return new Date(rawDate)
    .toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short'
    })
    .replace(',', '');
};

export const generateSearchUrl = (obj: object): string => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(obj)) {
    let paramValue = value;
    if (typeof value === 'object') {
      paramValue = JSON.stringify(value);
    }
    searchParams.append(key, paramValue);
  }

  return searchParams.toString();
};

export const HIDE_NAV_PAGES = ['offline', 'login', 'reset', 'welcome'];

export const DEFAULT_PASSWORD_VALIDATION: PasswordValidation = {
  length: { text: 'Between 8 and 72 characters', valid: false },
  upper: { text: '1 uppercase letter', valid: false },
  lower: { text: '1 lowercase letter', valid: false },
  digit: { text: '1 number', valid: false },
  special: { text: '1 special character [#?!@$%^&*-]', valid: false }
};

export const validatePassword = (formData: PasswordFormData) => {
  const result: { errorMsg: string; strengthUpdate: string; validationUpdate: PasswordValidation } = {
    errorMsg: '',
    strengthUpdate: '',
    validationUpdate: structuredClone(DEFAULT_PASSWORD_VALIDATION)
  };

  const length = formData.password.length;
  const password = formData.password;
  const confirm = formData.password_confirmation;
  const specialCharRegex = /[#?!@$%^&*-]/g;
  const numberRegex = /[0-9]/g;
  const lowerRegex = /[a-z]/;
  const upperRegex = /[A-Z]/;

  //Length
  if (length && length >= 8 && length <= 72) result.validationUpdate.length.valid = true;

  //UpperCase
  if (password && upperRegex.test(password)) result.validationUpdate.upper.valid = true;

  //LowerCase
  if (password && lowerRegex.test(password)) result.validationUpdate.lower.valid = true;

  //Special Char
  if (password && specialCharRegex.test(password)) result.validationUpdate.special.valid = true;

  //Numbers
  if (password && numberRegex.test(password)) result.validationUpdate.digit.valid = true;

  //Strength & Match: all cases above must pass to trigger this block.
  if (length && Object.values(result.validationUpdate).every((item) => item.valid)) {
    const specialLength = (password?.match(specialCharRegex) || []).length;
    const digitLength = (password?.match(numberRegex) || []).length;

    if (length === 8) result.strengthUpdate = 'weak';
    if (length > 10) result.strengthUpdate = 'fair';
    if (length > 12 && specialLength > 1 && digitLength > 1) result.strengthUpdate = 'strong';
    if (password && confirm && password !== confirm) result.errorMsg = 'Passwords do not match';
  }

  return result;
};

export const getSFHost = () => {
  return process.env.REACT_APP_SALESFORCE_INSTANCE_URL;
};

export const buildAdvancedQueryURL = (filters: ApiAdvancedFilter, baseUrl: string): string => {
  let url = `${process.env.REACT_APP_API_SERVER_URL}/${baseUrl}?`;
  const urlParams = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'filters') {
      value.forEach((filter: FilterableField, index: number) => {
        const formattedValue = Array.isArray(filter.value) ? filter.value.join(',') : filter.value;
        const filterKey = `filters[${index}]`;
        urlParams.append(`${filterKey}[field]`, filter.field);
        urlParams.append(`${filterKey}[value]`, String(formattedValue));
        urlParams.append(`${filterKey}[operator]`, filter.operator);
      });
      if (filters.logicalOperator) urlParams.append('logicalOperator', filters.logicalOperator);
    } else if (value !== undefined && value !== null) {
      urlParams.append(key, String(value));
    }
  }
  if (urlParams) url += urlParams.toString();
  return url;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getObjValidity = (data: any, requiredKeys: string[]) => {
  const missingProperties = Object.keys(data)
    .filter((key) => requiredKeys.includes(key) && !data[key])
    .map((key) => key);

  return { errors: missingProperties, valid: missingProperties.length === 0 };
};

export const capitalizeText = (text: string): string => text[0].toUpperCase() + text.slice(1).toLowerCase();

export const sortWidgetCustomizations = (customizations: WidgetCustomization[]): WidgetCustomization[] => {
  return customizations.sort((widgetDefOne: WidgetCustomization, widgetDefTwo: WidgetCustomization) => {
    return widgetDefOne?.index - widgetDefTwo?.index;
  });
};

export const getValuesForColumn = (
  rows: GridValidRowModel[],
  field: string,
  compare?: (a: string | number, b: string | number) => number
) => {
  const seen = new Set();

  return rows
    .reduce((acc, ele: GridValidRowModel) => {
      let val = field.includes('.') ? getNestedValue(ele, field) : ele[field];

      // Will need a better way to do this if there are more transforms in the future.
      if (field === 'account_id') {
        val = ele.accountName || getUserAccountById(val)?.name;
      }

      // Synthesized by the column's valueGetter, so the raw row value is not the filterable one.
      if (field === 'origin') {
        val = getOriginLabel(
          isUpstackSourced(ele),
          isConvertedRecord(ele.entry_method),
          ele.accountName || getUserAccountById(ele.account_id)?.name
        );
      }

      if (field.includes('product.')) {
        val = ele.product?.nameAbbreviation || ele.product?.name || '';
      }

      if (field === 'contractEndDate') {
        val = formatContractEndDate(val, ele.term);
      }

      // Matches the column's valueGetter, or the two month-to-month spellings would offer two
      // identical-looking options that each match only half the rows.
      if (field === 'term') {
        val = termLabel(val);
      }

      if (field === 'created_at') {
        const createdAt = val ? new Date(val) : null;
        val = createdAt && isValid(createdAt) ? format(createdAt, 'MM/dd/yyyy h:mm a') : '';
      }

      if ((val || val === 0) && !seen.has(val)) {
        seen.add(val);
        acc.push(val);
      }

      return acc;
    }, [])
    .sort(compare);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const arrayMove = (arr: any[], from: number, to: number) => {
  const element = arr[from];
  arr.splice(from, 1);
  arr.splice(to, 0, element);
};

export const getTabListItemClassName = (index: number, selectedIndex?: number): string => {
  return `${
    index === selectedIndex ? 'border-transparent bg-grey-0 border-b-0' : 'text-gray-500 hover:text-gray-700'
  } whitespace-nowrap py-4 px-5 font-medium text-sm`;
};

export const getTabs = (obj: UserPreferencesContent, prefix: string, customOnly?: boolean): CustomView[] => {
  const allTabs = Object.keys(obj)
    .filter((key) => key.startsWith(prefix))
    .map((key) => ({ page: prefix, name: key.substring(prefix.length) }))
    .sort((a, b) => (a.name < b.name ? -1 : 1));

  const customTabs = Object.keys(obj)
    .filter((key) => key.startsWith(prefix) && ((obj?.[key as PrefKey] || {}) as GridPreferences).customView)
    .map((key) => ({ page: prefix, name: key.substring(prefix.length) }))
    .sort((a, b) => (a < b ? -1 : 1));

  return customOnly ? customTabs : allTabs;
};

// A destination carries its own query, so it only survives being nested in another URL encoded.
export const redirectToQuery = (destination: string): string => `redirectTo=${encodeURIComponent(destination)}`;

// The origin comparison, not the leading slash, is what keeps this on our own site: separators
// the URL parser reads as the start of an authority — the backslash in "/\evil.com" — pass a
// startsWith('/') test and still navigate away.
export const safePathName = (path: string): string => {
  if (!path.startsWith('/')) return '/';

  try {
    const target = new URL(path, window.location.origin);
    const resolved = `${target.pathname}${target.search}`;

    // Folding a dot segment can leave a rooted path starting "//", which reads as a host again
    // the next time anything resolves it.
    if (resolved.startsWith('//')) return '/';

    return target.origin === window.location.origin ? resolved : '/';
  } catch {
    return '/';
  }
};

export const geoCodeAddress = (address: string): Promise<google.maps.GeocoderResult> => {
  const geocoder = new window.google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        resolve(results[0]);
      } else {
        reject(new Error(`Geocoding failed for address "${address}" with status: ${status}`));
      }
    });
  });
};
